import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthProvider";
import { handleSnapshotError } from "../utils/recoveryErrors";

function isPermissionDeniedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" && code.toLowerCase().includes("permission-denied");
}

/**
 * Wallet data structure
 */
export interface WalletData {
  currentTokens: number;
  lifetimeTokensGranted: number;
  lifetimeTokensSpent: number;
  lastUpdated: Date | null;
}

/**
 * Return type for useWallet hook
 */
export interface UseWalletReturn {
  /** Current wallet data */
  wallet: WalletData | null;
  /** Whether wallet data is still being loaded */
  loading: boolean;
  /** Error message, if any */
  error: string | null;
}

/**
 * Hook to observe wallet data in real-time
 *
 * Listens to the wallets/{uid} document and provides
 * real-time updates of the user's token balance.
 */
export function useWallet(): UseWalletReturn {
  const { user, loading: authLoading } = useAuth();
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If auth is still loading, wait
    if (authLoading) {
      Promise.resolve().then(() => {
        setLoading(true);
      });
      return;
    }

    // If no user, reset wallet data
    if (!user) {
      Promise.resolve().then(() => {
        setWallet(null);
        setLoading(false);
        setError(null);
      });
      return;
    }

    // Set up real-time listener for wallet document
    const walletRef = doc(db, "wallets", user.uid);

    const unsubscribe = onSnapshot(
      walletRef,
      (snapshot) => {
        setLoading(false);
        setError(null);

        if (snapshot.exists()) {
          const data = snapshot.data();
          setWallet({
            currentTokens: data.currentTokens || 0,
            lifetimeTokensGranted: data.lifetimeTokensGranted || 0,
            lifetimeTokensSpent: data.lifetimeTokensSpent || 0,
            lastUpdated: data.lastUpdated?.toDate() || null,
          });
        } else {
          // Wallet document doesn't exist yet
          setWallet({
            currentTokens: 0,
            lifetimeTokensGranted: 0,
            lifetimeTokensSpent: 0,
            lastUpdated: null,
          });
        }
      },
      (err) => {
        const applyErrorState = () => {
          setError(err.message);
          setLoading(false);
          setWallet(null);
        };
        // Permission-denied is the expected signed-out state — stay silent.
        if (isPermissionDeniedError(err)) {
          applyErrorState();
          return;
        }
        handleSnapshotError("wallet", err, applyErrorState);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading]);

  return {
    wallet,
    loading,
    error,
  };
}
